/*
 * TL CART QUANTITY - OPTIMISTIC UI + DEBOUNCED WRITE. Aug 11 2026.
 *
 * BUG (Robbie): "Adding units to items in the cart have a lag between each item
 * added. To go from 1 up to 5 you have to click, wait for it to update to 2,
 * click again. A bit laggy."
 *
 * It is worse than lag. CLICKS WERE BEING SILENTLY LOST.
 *
 * snippets/quantity-selector.liquid renders the +/- controls as anchors with the
 * TARGET quantity baked into the href at render time, and theme.js reads the
 * number straight back out of that href. cart/change.js sets an ABSOLUTE
 * quantity, so until the section re-renders and rewrites the href, every extra
 * click re-sends the same number.
 *
 * MEASURED: three clicks on + at qty 1, 150ms apart, produced three POSTs all
 * carrying quantity:2. Final cart quantity was 2, not 4. Two clicks vanished.
 *
 * The wait is real too. Each click costs a round trip plus a full rebuild of the
 * drawer light DOM (replaceChildren), then TL's own chain: tlScheduleGwpSync
 * (300ms) -> GET cart.js -> attribute writes -> cart-drawer:refreshed -> syncGwp
 * (which may write and re-render again) -> tlRefreshCartPage (450ms). Roughly
 * 0.7-1.5s before the href advances and the next click can land.
 *
 * WHAT THIS DOES. Intercepts the click in the capture phase, updates the number
 * on screen immediately, rewrites both hrefs so the next click compounds, and
 * sends ONE cart/change.js with the final absolute quantity after the clicks
 * stop. Five taps is now one request instead of five, and none are lost.
 *
 * WHY CAPTURE PHASE. Prestige binds its handler in the bubble phase, so a
 * capture listener on document runs first and stopImmediatePropagation keeps the
 * native path from firing at all. Same technique and reasoning as
 * assets/tl-cart-event-fix.js.
 *
 * WHY THE PENDING MAP LIVES OUTSIDE THE DOM. Any other cart write during the
 * debounce window - the GWP add/remove in tl-club.js, tlScheduleGwpSync,
 * tlRefreshCartPage - triggers replaceChildren and wipes the input we just
 * typed into. reapplyPending() puts the value back on every refresh.
 *
 * ---------------------------------------------------------------------------
 * Aug 12 2026 - TWO RACES FIXED. Symptom (Robbie): "it glitches sometimes. It
 * will jump from 5 back to 4 and so forth."
 *
 * RACE 1 - THE UNANNOUNCED RE-RENDER. tlRefreshCartPage in
 * snippets/tl-cart-scripts.liquid replaces the whole main-cart section via the
 * Section Rendering API and used to dispatch NO event, so reapplyPending never
 * ran and the server's older number was painted over ours. Its 450ms timer and
 * this file's 450ms debounce collide almost exactly, which is why it was
 * intermittent. That file now dispatches 'tl:cart-rerendered' on every path that
 * replaces cart DOM, and we listen for it - but we no longer TRUST any event as
 * the only mechanism. A MutationObserver on the cart surfaces is the real safety
 * net: whatever replaces the DOM, announced or not, the pending value goes back.
 *
 * RACE 2 - THE IN-FLIGHT CLOBBER. flush() used to "delete pending[key]"
 * unconditionally when the request resolved. If the shopper clicked again while
 * that request was in flight, the response landed AFTER the new optimistic
 * number was painted, dropped the pending entry, and the next re-render happily
 * repainted the server's now-stale quantity - the same visible jump backwards.
 * flush() now remembers the quantity it sent and only clears pending[key] if it
 * still matches; otherwise the newer value stays and the next flush sends it.
 *
 * DELIBERATELY NOT DONE: no optimistic update of the progress bar or the line
 * price. Those come from the Liquid in snippets/tl-progress-bar.liquid, and
 * duplicating that message ladder in JS is exactly what caused the bar bug this
 * same day. The bar catches up on the real render, up to 450ms behind.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 450;
  var pending = {};
  var timers = {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function hasPending() {
    for (var k in pending) { if (Object.prototype.hasOwnProperty.call(pending, k)) { return true; } }
    return false;
  }

  /* The cart page renders TWO quantity selectors per line - a desktop block in
     sections/main-cart.liquid and a mobile one in snippets/line-item.liquid - so
     every update has to hit all of them or the two disagree. */
  function eachInputForKey(key, fn) {
    var nodes = document.querySelectorAll('input[data-line-key]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-line-key') === key) fn(nodes[i]);
    }
  }

  /* IDEMPOTENT ON PURPOSE - this is the MutationObserver loop guard. See the
     note above the observer. Writing only when the DOM disagrees means a
     re-application that has nothing to do produces no mutations at all, so the
     observer cannot re-trigger itself. */
  function setHref(link, key, qty) {
    if (!link || link.tagName !== 'A') return;
    var href = root() + 'cart/change?id=' + encodeURIComponent(key) + '&quantity=' + qty;
    if (link.getAttribute('href') === href) return;
    link.setAttribute('href', href);
  }

  /* Keep the markup honest even though we suppress the native handler: if this
     script is ever removed the hrefs still describe the right next step. */
  function paint(key, qty) {
    eachInputForKey(key, function (input) {
      if (input.value !== String(qty)) input.value = qty;
      var sel = input.closest('quantity-selector');
      if (!sel) return;
      var controls = [];
      for (var i = 0; i < sel.children.length; i++) {
        var t = sel.children[i].tagName;
        if (t === 'A' || t === 'BUTTON') controls.push(sel.children[i]);
      }
      setHref(controls[0], key, qty - 1);
      setHref(controls[controls.length - 1], key, qty + 1);
    });
  }

  function flush(key) {
    delete timers[key];
    var qty = pending[key];
    if (qty == null) return;

    /* THE VALUE WE ARE ABOUT TO PUT ON THE WIRE. Captured now so that when this
       promise resolves we can tell whether the shopper has clicked again since.
       Clearing pending[key] on a stale response is race 2 above. */
    var sentQty = qty;

    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: sentQty, sections: sections.join(',') })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/change failed');
        return r.json();
      })
      .then(function (changed) {
        return fetch(root() + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            cart.sections = changed.sections;
            return cart;
          });
      })
      .then(function (cart) {
        /* Only retire the optimistic value if it is still the value we sent. If
           the shopper clicked again while this was in flight, pending[key] now
           holds a NEWER number with its own debounce timer already armed - leave
           it alone and let that flush win. Dropping it here is what let the
           subsequent re-render repaint the server's stale quantity. */
        if (pending[key] === sentQty) {
          delete pending[key];
        } else {
          /* Re-assert the newer optimistic number over the DOM that the
             cart:change below is about to rebuild. */
          paint(key, pending[key]);
        }
        /* A complete detail.cart with .sections is mandatory - a malformed one
           throws in CartDrawer, CartCount, CartDot and FreeShippingBar. See the
           header of assets/tl-cart-event-fix.js. */
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'quantity:change', cart: cart } })
        );
      })
      .catch(function () {
        /* Never leave an optimistic number the server never accepted. A refresh
           forces theme.js to re-render from the real cart. But if a newer click
           has landed since, that one has not been rejected - keep it pending so
           its own flush still runs. */
        if (pending[key] === sentQty) {
          delete pending[key];
        }
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { baseEvent: 'quantity:change' } })
        );
      });
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var link = event.target.closest('quantity-selector a[href*="cart/change"]');
      if (!link) return;

      var sel = link.closest('quantity-selector');
      var input = sel && sel.querySelector('input[data-line-key]');
      /* No data-line-key means this is the PDP / quick-order selector, which is
         not a cart line and must keep its native behaviour. */
      if (!input) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var key = input.getAttribute('data-line-key');
      var step = parseInt(input.getAttribute('step'), 10) || 1;
      var maxAttr = input.getAttribute('max');
      var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;

      /* Direction by DOM position, not by the href value and not by the label.
         The href is stale by definition once we start compounding, and the
         sr-only text is translated. Minus is always the first control, plus the
         last. */
      var controls = [];
      for (var i = 0; i < sel.children.length; i++) {
        var t = sel.children[i].tagName;
        if (t === 'A' || t === 'BUTTON') controls.push(sel.children[i]);
      }
      var dir = controls.indexOf(link) === 0 ? -1 : 1;

      var current = pending[key] != null ? pending[key] : parseInt(input.value, 10) || 0;
      var next = current + dir * step;

      /* Floor is 0, NOT the input's min. On a cart line, minus at quantity 1
         posts quantity 0 and removes the line - clamping to the variant's
         quantity_rule.min (usually 1) would break removal entirely. */
      if (next < 0) next = 0;
      if (next > max) next = max;
      if (next === current) return;

      pending[key] = next;
      paint(key, next);
      ensureObserved();

      if (timers[key]) clearTimeout(timers[key]);

      /* Removing is a deliberate act and should feel instant. Everything else
           waits for the clicking to stop. */
      if (next === 0) {
        flush(key);
        return;
      }
      timers[key] = setTimeout(function () { flush(key); }, DEBOUNCE_MS);
    },
    true
  );

  /* Any render during the debounce window replaces the input we typed into.
     Put the pending value back, and drop any line the server has since removed
     so we can never resurrect it. */
  function reapplyPending() {
    Object.keys(pending).forEach(function (key) {
      var found = false;
      eachInputForKey(key, function () { found = true; });
      if (!found) {
        if (timers[key]) { clearTimeout(timers[key]); delete timers[key]; }
        delete pending[key];
        return;
      }
      paint(key, pending[key]);
    });
  }

  /* ------------------------------------------------------------------------
     RE-APPLICATION TRIGGERS

     Events first, because they are precise and cheap. 'tl:cart-rerendered' is
     dispatched by snippets/tl-cart-scripts.liquid from every path in that file
     that replaces cart DOM (Aug 12 2026); the other two come from the theme and
     from tl-club.js.
     ------------------------------------------------------------------------ */
  function onCartEvent() {
    ensureObserved();
    reapplyPending();
  }

  document.addEventListener('cart-drawer:refreshed', onCartEvent, true);
  document.addEventListener('cart:change', onCartEvent, true);
  document.addEventListener('tl:cart-rerendered', onCartEvent, true);

  /* ------------------------------------------------------------------------
     ...AND THE SAFETY NET. An event contract can be broken by any future edit,
     by an app block, or by a theme update. The DOM cannot lie about having been
     replaced. While anything is pending we watch the cart surfaces and put the
     value back on any mutation, announced or not.

     RE-ENTRANCY: chosen mechanism is VALUE EQUALITY, not a flag and not
     disconnect/reconnect. Reason: MutationObserver callbacks are delivered as a
     microtask AFTER the current task finishes, so a synchronous flag raised and
     lowered around the writes is already back down by the time the callback
     runs - it would guard nothing. disconnect/reconnect works but has to be
     paired with takeRecords() and leaves a window where a real third-party
     re-render goes unseen. Instead paint()/setHref() write only when the DOM
     disagrees with the pending value, so a re-application that has nothing to do
     produces zero mutation records and the loop terminates after at most one
     extra no-op callback. (Belt and braces: we observe childList/subtree only,
     and input.value is a property while href is an attribute, so neither of our
     writes is even in the observed set.)

     WHAT WE OBSERVE. The drawer element survives its own replaceChildren, so we
     can observe it directly. The /cart container does NOT - tlRefreshCartPage
     overwrites the innerHTML of the enclosing shopify-section, destroying
     .tl-cart-page itself - so we observe the section wrapper, which persists.
     ------------------------------------------------------------------------ */
  var observer = null;
  var reapplyScheduled = false;

  function observerTargets() {
    var out = [];
    function add(el) { if (el && out.indexOf(el) === -1) out.push(el); }

    var i;
    var drawers = document.querySelectorAll('cart-drawer, .cart-drawer');
    for (i = 0; i < drawers.length; i++) add(drawers[i]);

    var pages = document.querySelectorAll('.tl-cart-page');
    for (i = 0; i < pages.length; i++) add(pages[i].closest('[id^="shopify-section-"]') || pages[i]);

    /* Nothing cart-shaped in the document yet (drawer not rendered, or a theme
       change renamed the containers): fall back to the body so the net still
       catches the re-render. The callback is a no-op unless something is
       pending, so the cost is negligible. */
    if (!out.length && document.body) add(document.body);

    return out;
  }

  /* observe() on a node we are already observing just replaces its options, so
     calling this repeatedly is safe and picks up surfaces that appeared later. */
  function ensureObserved() {
    if (!observer) return;
    var targets = observerTargets();
    for (var i = 0; i < targets.length; i++) {
      observer.observe(targets[i], { childList: true, subtree: true });
    }
  }

  if (window.MutationObserver) {
    observer = new MutationObserver(function () {
      if (!hasPending()) return;
      if (reapplyScheduled) return;
      reapplyScheduled = true;
      /* Coalesce a burst of records from one re-render into a single pass, and
         let the re-render finish before we read the DOM. */
      window.setTimeout(function () {
        reapplyScheduled = false;
        ensureObserved();
        reapplyPending();
      }, 0);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ensureObserved, { once: true });
    } else {
      ensureObserved();
    }
  }
})();
